import * as amplitude from '@amplitude/analytics-browser';
import { track } from '@amplitude/analytics-browser';
import { LocationStrategy } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  PaymentIntentResult,
  PaymentRequest,
  StripeElements,
  StripeError,
} from '@stripe/stripe-js';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { ModalComponent, ModalData } from 'shared/src/components/modal/modal.component';
import { ApiService, CurrencyCode, StripePurchaseInput } from './api.service';
import { CustomizerSettingsService } from './customizer-settings.service';
import { LoadingService } from './loading.service';
import { PaymentIntentHandlerResult, StripeService } from './stripe.service';
import { UserInfoService } from './user-info.service';

export interface SubscriptionParams {
  productId: string;
  email?: string;
  customerId?: string;
  name?: string;
  amplitudeDeviceId?: string;
}

export type PaymentFlow = 'subscription' | 'payment';

@Injectable({
  providedIn: 'root',
})
export class PaymentHandlerService {
  constructor(
    private api: ApiService,
    private router: Router,
    private locationStrategy: LocationStrategy,
    private dialog: MatDialog,
    private loadingService: LoadingService,
    private stripeService: StripeService,
    private themeService: CustomizerSettingsService,
    private userInfo: UserInfoService
  ) {}

  /**
   * Creates a subscription and returns the client secret
   */
  createSubscription(params: SubscriptionParams): Observable<{
    clientSecret: string;
    isSetupIntent: boolean;
    stripeCustomerId: string;
  }> {
    // Persist the identity the subscription is filed under: some hosts (e.g. the holiday
    // discount paywall) resolve an email and pass it here without ever storing it, and the
    // result URL / result page read it from UserInfoService.
    if (params.email) {
      this.userInfo.setEmail(params.email);
    }

    const subscriptionParams: StripePurchaseInput = {
      productId: params.productId,
      email: params.email,
      customerId: params.customerId,
      name: params.name,
      amplitudeDeviceId: params.amplitudeDeviceId || amplitude.getDeviceId(),
    };

    return this.api.createStripeSubscription(subscriptionParams);
  }

  /**
   * Handles payment with Apple Pay / Google Pay
   */
  handleExpressPayment(
    clientSecret: string,
    paymentMethod: any,
    insufficientFundsAction?: () => void,
    publishableKey?: string,
    flow: PaymentFlow = 'subscription'
  ): Observable<PaymentIntentHandlerResult> {
    return this.stripeService
      .confirmExpressPayment(clientSecret, paymentMethod, publishableKey)
      .pipe(
        tap(result => {
          if (!result.success && result.error) {
            this.handlePaymentError(
              result.error,
              insufficientFundsAction,
              undefined,
              undefined,
              flow
            );
          }
        }),
        switchMap(result => {
          // If payment requires additional actions, handle them
          if (result.success && result.paymentIntent?.status === 'requires_action') {
            return this.handleAdditionalPaymentActions(clientSecret, publishableKey, flow);
          }

          return of(result);
        })
      );
  }

  /**
   * Handles additional authentication actions if needed
   */
  handleAdditionalPaymentActions(
    clientSecret: string,
    publishableKey?: string,
    flow: PaymentFlow = 'subscription'
  ): Observable<PaymentIntentHandlerResult> {
    return this.stripeService.handleAdditionalPaymentActions(clientSecret, publishableKey).pipe(
      tap(result => {
        if (!result.success && result.error) {
          this.handlePaymentError(result.error, undefined, undefined, undefined, flow);
        }
      })
    );
  }

  /**
   * Handles regular checkout payment confirmation
   */
  confirmPayment(
    elements: StripeElements,
    clientSecret: string,
    returnUrl: string,
    publishableKey?: string,
    flow: PaymentFlow = 'subscription'
  ): Observable<PaymentIntentResult> {
    this.loadingService.start();

    return this.stripeService
      .confirmPayment(elements, clientSecret, returnUrl, publishableKey)
      .pipe(
        tap(result => {
          this.loadingService.finish();
          if (result.error) {
            this.handlePaymentError(result.error, undefined, undefined, undefined, flow);
          }
        }),
        catchError(error => {
          this.loadingService.finish();
          this.handlePaymentError(error, undefined, undefined, undefined, flow);
          throw error;
        })
      );
  }

  /**
   * Creates a payment request object for Apple Pay / Google Pay
   */
  createPaymentRequest(
    amount: number,
    label: string,
    currency: CurrencyCode,
    requestEmail: boolean = false,
    publishableKey?: string
  ): Observable<PaymentRequest> {
    return this.stripeService.createPaymentRequest(
      amount,
      label,
      currency,
      requestEmail,
      publishableKey
    );
  }

  /**
   * Creates payment elements for Stripe Elements
   */
  createPaymentElements(
    amount: number,
    currency: CurrencyCode,
    mode: 'payment' | 'subscription' = 'subscription',
    publishableKey?: string
  ): Observable<StripeElements> {
    return this.stripeService.createPaymentElements(
      amount,
      currency,
      this.themeService.isDark(),
      mode,
      publishableKey
    );
  }

  /**
   * `redirect` is an in-app path the result page offers as a "keep going here" exit, set only
   * by hosts that have somewhere to send the user (the web app). Funnels leave it undefined
   * and the result page keeps ending on the app-store step.
   */
  private paymentResultQueryParams(
    productCode: string,
    stripeCustomerId: string,
    redirect?: string
  ): Record<string, string> {
    const queryParams: Record<string, string> = {
      product_code: productCode,
      stripeCustomerId,
    };

    // The result page can load in a context that never saw the checkout (a Stripe redirect
    // bounced to another browser, a re-opened link), where localStorage is empty — the URL
    // is then the only identity carrier, and the result page hydrates UserInfoService from it.
    const email = this.userInfo.getEmail();
    if (email) {
      queryParams['email'] = email;
    }

    if (redirect) {
      queryParams['redirect'] = redirect;
    }

    return queryParams;
  }

  /**
   * Navigates to the success page after payment
   */
  navigateToSuccess(productCode: string, stripeCustomerId: string, redirect?: string): void {
    this.router.navigate(['payment-result'], {
      queryParams: this.paymentResultQueryParams(productCode, stripeCustomerId, redirect),
      // Marks a genuine checkout arrival: the result page only tracks the purchase when this
      // flag (or Stripe's redirect_status) is present, so re-opened result URLs stay silent.
      state: { fromCheckout: true },
    });
  }

  /**
   * Builds an absolute URL to the payment result route, respecting the app's base href.
   * This is important for localized deployments (e.g. /es/) so Stripe returns users to the
   * correct locale context after redirect-based flows.
   */
  getPaymentResultReturnUrl(
    productCode: string,
    stripeCustomerId: string,
    redirect?: string
  ): string {
    const serialized = this.router.serializeUrl(
      this.router.createUrlTree(['payment-result'], {
        queryParams: this.paymentResultQueryParams(productCode, stripeCustomerId, redirect),
      })
    );

    // Adds base href (e.g. "/es/") if present.
    const externalUrl = this.locationStrategy.prepareExternalUrl(serialized);

    return new URL(externalUrl, window.location.origin).toString();
  }

  /**
   * Handles payment errors
   */
  handlePaymentError(
    error: StripeError | HttpErrorResponse | any,
    insufficientFundsAction?: () => void,
    email?: string,
    productCode?: string,
    flow: PaymentFlow = 'subscription',
    redirect?: string
  ): void {
    // Subscription API failures arrive as HttpErrorResponse. The duplicate-subscription
    // case gets a dedicated message and redirect; every other API error falls through to
    // the generic payment-error handling below so the user still gets an explanation.
    if (
      flow === 'subscription' &&
      error instanceof HttpErrorResponse &&
      error.status === 400 &&
      error.error?.code === 'DUPLICATE_SUBSCRIPTION'
    ) {
      // Tracking for this case lives in PostPaymentFlowComponent (the result page we
      // navigate to below), which owns success/failure tracking for flows that land there.
      this.loadingService.finish();

      // Persist the identity so the result page's app-download deep link still works for
      // flows (e.g. discount paywalls) that pass email into checkout without storing it.
      if (email) {
        this.userInfo.setEmail(email);
      }

      const message = email
        ? $localize`:@@duplicateSubscriptionError:You already have an active subscription for ${email}:email:. Please contact support@getpeech.com if you need help.`
        : $localize`:@@duplicateSubscriptionErrorNoEmail:You already have an active subscription. Please contact support@getpeech.com if you need help.`;

      this.dialog
        .open<ModalComponent, ModalData>(ModalComponent, {
          data: {
            title: $localize`:@@upsellSorryTitle:Sorry`,
            message,
            rive: {
              fileName: 'credit_card',
              animations: ['Timeline 1'],
              ratio: 1.1 / 1,
            },
          },
        })
        .afterClosed()
        .pipe(
          tap(() => {
            // Carries the host's return path too: an app user who already has a
            // subscription still needs a way back in, not a dead end on the store screen.
            this.router.navigate(['payment-result'], {
              queryParams: {
                skip_upsells: 'true',
                product_code: productCode,
                ...(redirect ? { redirect } : {}),
              },
              // Same arrival marker as navigateToSuccess — the result page's failure tracking
              // is behind the same gate as success tracking.
              state: { fromCheckout: true },
            });
          })
        )
        .subscribe();
      return;
    }

    console.error('Payment error:', error);

    if (this.isNonFailureError(error)) {
      return;
    }

    // One-time payments (credits top-up) own their failure tracking — counting them here
    // would distort subscription conversion metrics.
    if (flow === 'subscription') {
      track('web_subscribe_failure', {
        error: error.code || error.message,
      });
    }

    if (error.decline_code === 'insufficient_funds' && insufficientFundsAction) {
      insufficientFundsAction();
      return;
    }

    // HttpErrorResponse.message is a raw "Http failure response for ..." string, so fall
    // back to a friendly generic message for any non-Stripe (API/network) error.
    const genericFailureMessage = $localize`:@@paymentFailedError:Your payment has failed. Please try again.`;
    let message =
      error instanceof HttpErrorResponse
        ? genericFailureMessage
        : error.message || genericFailureMessage;

    // TODO: implement quick PayPal button for this case and push them there
    if (error.code === 'do_not_honor') {
      message =
        'Your payment failed. Please consider using another payment method, or just try one more time.';
    }

    this.dialog
      .open<ModalComponent, ModalData>(ModalComponent, {
        data: {
          title: $localize`:@@upsellSorryTitle:Sorry`,
          message,
          rive: {
            fileName: 'credit_card',
            animations: ['Timeline 1'],
            ratio: 1.1 / 1,
          },
        },
      })
      .afterClosed()
      .subscribe();
  }

  /**
   * True for errors that are not payment failures: form validation (incomplete card fields)
   * and Stripe element lifecycle noise. Callers use it to keep failure metrics clean.
   */
  isNonFailureError(error: StripeError | any): boolean {
    return error?.type === 'validation_error' || this.isElementLifecycleError(error);
  }

  private isElementLifecycleError(error: StripeError): boolean {
    const message = (error?.message || '').toLowerCase();

    return (
      message.includes('component destroyed') ||
      message.includes('we could not retrieve data from the specified element') ||
      message.includes('could not retrieve elements store due to unexpected error')
    );
  }
}
